import { createContext, useState, useEffect, useContext, PropsWithChildren, Dispatch, SetStateAction } from 'react';
import { updateActiveTask, Util } from '../../util/Util';
import { v4 as uuidv4 } from 'uuid';
import { BatchTask } from "../../util/Batch";
import { ItemInterface, Item as MyItem } from '../../util/Item';
import { useGridApiRef } from "@mui/x-data-grid";
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { businessToday } from "../../util/businessClock";

import { addMoney, roundMoney, roundQty, lineTotal } from "../../util/quantity";
export interface InventoryTask<T> extends BatchTask<T> {
	depot: number;
	invType: "EOD" | "IP" | "SOD" | null;
}

export class Item extends MyItem {
	sector: string;
	category: string;

	constructor(data: ItemInterface & { sector?: string, category?: string } = {}) {
		super(data);

		this.sector = data.sector ?? '';
		this.category = data.category ?? '';
	}

	get stockDifference() {
		return roundQty(Number(this.quantity) - Number(this.stock));
	}

	get oldValue() {
		return lineTotal(this.stock, this.price);
	}

	get valueDifference() {
		return roundMoney(this.total - this.oldValue);
	}

	get uniqueId() {
		return `${this.id}`;
	};
}

interface ContextProps {
	pendingTasks: InventoryTask<Item>[];
	setPendingTasks: Dispatch<SetStateAction<InventoryTask<Item>[]>>;
	fetchedTask: any | null;
	setFetchedTask: (task: any) => void;
	addNewTask: () => void;
	activeTask: InventoryTask<Item> | undefined;
	items: any[];
	setItems: Dispatch<SetStateAction<any[]>>;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
}

export const InventoryContext = createContext<ContextProps | undefined>(undefined);

interface ProviderProps {
}

export const InventoryProvider = ({ children }: PropsWithChildren<ProviderProps>) => {
	const [pendingTasks, setPendingTasks] = useState<InventoryTask<Item>[]>([]);
	const [fetchedTask, setFetchedTask] = useState<any>(null);
	const [items, setItems] = useState<any[]>([]);

	const apiRef = useGridApiRef();
	const activeTask = pendingTasks?.find((task) => task.active);

	const addNewTask = () => {
		const newTask: InventoryTask<Item> = {
			id: uuidv4(),
			items: [],
			active: true,
			date: businessToday(),
			reference: Util.getInventoryNumber(),
			receiver: -1,
			depot: -1,
			invType: null,
			description: "",
			is_public: false,
			server_v: 0,
			createdAt: new Date(),
			updatedAt: new Date(),
			total: function () {
				return this.items!.reduce((sum, item) => addMoney(sum, item.total), 0);
			},
		};

		setPendingTasks((prevTasks) =>
			prevTasks.map((task) => ({ ...task, active: false })).concat(newTask)
		);

		setItems([]);
	};

	useEffect(() => {
		if (fetchedTask) {
			const itemRecords = fetchedTask.records.map((record: any) => {
				const item = new Item(
					{
						id: record.item_id,
						item_code: record.item_code,
						item: record.item_name,
						stock: record.snap_shot,
						price: record.amount,
						quantity: record.quantity,
						sector: record.sector,
						category: record.category,
						isFractional: record.is_fractional === true || record.IsFractional === true || items.find((c: any) => c.id === record.item_id)?.IsFractional === true,
					}
				);

				return item;
			});

			const { batch, date, depot_id, client_id, reference, description } = fetchedTask.details;
			updateActiveTask(setPendingTasks, {
				...activeTask,
				id: batch,
				items: itemRecords,
				date: date,
				depot: depot_id,
				receiver: client_id,
				reference: reference,
				description: description,
			});
		}
	}, [fetchedTask]);



	const value: ContextProps = {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		addNewTask,
		activeTask,
		items,
		setItems,
		apiRef
	};

	return (
		<InventoryContext.Provider value={value}>
			{children}
		</InventoryContext.Provider>
	);
};

export const useInventory = () => {
	const context = useContext(InventoryContext);
	if (!context) {
		throw new Error('useInventory must be used within a InventoryProvider');
	}
	return context;
};